/** Back office → Billing: invoices and quotes, payments, figures to collect, new quote / invoice. */

import {
  type BillingInvoicesListParams,
  type BillingPaymentsListParams,
  billingInvoicesList,
  billingPaymentsList,
  type Invoice,
  type InvoiceKindEnum,
  type InvoiceStatusEnum,
  type PaymentMethodEnum,
  type PaymentStatusEnum,
  useBillingInvoicesList,
  useBillingPaymentsList,
} from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Badge, Button, GlassCard, SelectField, Skeleton, SoftToggle } from "@agencyos/ui-web";
import { useQuery } from "@tanstack/react-query";
import { AlarmClock, HandCoins, Plus, Wallet } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Page } from "../components/AppShell";
import { InvoiceFormSheet } from "./billing/InvoiceFormSheet";
import {
  dateText,
  dateTimeText,
  Figure,
  INVOICE_STATUSES,
  invoiceTone,
  isOverdue,
  monthInDouala,
  Pager,
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  paymentTone,
  todayInDouala,
  useBillingRoles,
} from "./billing/shared";

type Tab = "invoices" | "quotes" | "credits" | "payments";
const TABS: Tab[] = ["invoices", "quotes", "credits", "payments"];
const MAX_PAGES = 40;

/** The list endpoints also filter on these query parameters (not described in the OpenAPI schema). */
type InvoiceFilters = BillingInvoicesListParams & { kind?: InvoiceKindEnum; status?: InvoiceStatusEnum; unpaid?: "1" };
type PaymentFilters = BillingPaymentsListParams & { status?: PaymentStatusEnum; method?: PaymentMethodEnum };

/** Figures of the header: every unpaid invoice and this month's confirmed payments (all pages). */
function useFigures() {
  const unpaid = useQuery({
    queryKey: ["/api/v1/billing/invoices/", "figures"],
    queryFn: async () => {
      const rows: Invoice[] = [];
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const params: InvoiceFilters = { unpaid: "1", page };
        const response = await billingInvoicesList(params);
        rows.push(...response.data.results);
        if (!response.data.next) break;
      }
      return rows;
    },
  });
  const collected = useQuery({
    queryKey: ["/api/v1/billing/payments/", "figures"],
    queryFn: async () => {
      const month = monthInDouala(new Date().toISOString());
      let total = 0;
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const params: PaymentFilters = { status: "confirmed", page };
        const response = await billingPaymentsList(params);
        let older = false;
        for (const payment of response.data.results) {
          const when = payment.confirmed_at ?? payment.created_at;
          if (monthInDouala(when) === month) total += payment.amount_xaf;
          if (monthInDouala(payment.created_at) < month) older = true;
        }
        // Newest first: once a whole page is older than this month, stop.
        if (!response.data.next || older) break;
      }
      return total;
    },
  });
  const today = todayInDouala();
  const rows = unpaid.data;
  return {
    toCollect: rows?.reduce((sum, invoice) => sum + invoice.balance_xaf, 0),
    unpaidCount: rows?.length,
    overdue: rows?.filter((invoice) => invoice.due_date !== null && invoice.due_date < today).length,
    collected: collected.data,
  };
}

function InvoiceTable({ kind }: { kind: InvoiceKindEnum }) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<"" | InvoiceStatusEnum>("");
  const [unpaid, setUnpaid] = useState(false);
  const [page, setPage] = useState(1);
  const params: InvoiceFilters = {
    kind,
    page,
    ...(status ? { status } : {}),
    ...(unpaid && kind === "invoice" ? { unpaid: "1" as const } : {}),
  };
  const list = useBillingInvoicesList(params);
  const data = list.data?.status === 200 ? list.data.data : undefined;
  const statuses =
    kind === "quote"
      ? INVOICE_STATUSES.filter((s) => s !== "partial" && s !== "paid")
      : kind === "credit"
        ? INVOICE_STATUSES.filter((s) => s === "issued")
        : INVOICE_STATUSES.filter((s) => s !== "accepted");
  return (
    <GlassCard as="section" className="stack">
      <div className="row">
        <SelectField
          label={t("billing.filters.status")}
          value={status}
          options={[{ value: "", label: t("billing.filters.all") }, ...statuses.map((s) => ({ value: s, label: t(`billing.status.${s}`) }))]}
          onChange={(e) => {
            setStatus(e.target.value as "" | InvoiceStatusEnum);
            setPage(1);
          }}
        />
        {kind === "invoice" ? (
          <SoftToggle
            label={t("billing.filters.unpaid")}
            checked={unpaid}
            onChange={(value) => {
              setUnpaid(value);
              setPage(1);
            }}
          />
        ) : null}
      </div>
      {list.isPending ? <Skeleton style={{ height: 120 }} /> : null}
      {list.isError ? <p role="alert" className="ko">{t("common.error")}</p> : null}
      {data && !data.results.length ? <p className="muted">{kind === "quote" ? t("billing.noQuote") : kind === "credit" ? t("billing.noCredit") : t("billing.noInvoice")}</p> : null}
      {data?.results.length ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t("billing.col.number")}</th>
                <th>{t("billing.col.client")}</th>
                <th>{t("billing.col.date")}</th>
                {kind === "invoice" ? <th>{t("billing.col.due")}</th> : null}
                <th className="num">{t("billing.col.total")}</th>
                {kind === "invoice" ? <th className="num">{t("billing.col.balance")}</th> : null}
                <th>{t("billing.col.status")}</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <Link className="link nowrap" to={`/invoices/${invoice.id}`}>
                      {invoice.number}
                    </Link>
                    {invoice.contract_number ? <div className="muted">{invoice.contract_number}</div> : null}
                  </td>
                  <td>{invoice.client_name}</td>
                  <td className="nowrap">{dateText(invoice.created_at, i18n.language)}</td>
                  {kind === "invoice" ? <td className="nowrap">{dateText(invoice.due_date, i18n.language)}</td> : null}
                  <td className="num nowrap">{formatXaf(invoice.total_ttc_xaf)}</td>
                  {kind === "invoice" ? <td className="num nowrap">{formatXaf(invoice.balance_xaf)}</td> : null}
                  <td>
                    <div className="row">
                      <Badge tone={invoiceTone(invoice.status)}>{t(`billing.status.${invoice.status}`)}</Badge>
                      {isOverdue(invoice) ? <Badge tone="danger">{t("billing.overdue")}</Badge> : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <Pager
        page={page}
        hasNext={Boolean(data?.next)}
        onChange={setPage}
        labels={{ previous: t("billing.previous"), next: t("billing.next"), page: t("billing.page", { page }) }}
      />
    </GlassCard>
  );
}

function PaymentTable() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<"" | PaymentStatusEnum>("");
  const [method, setMethod] = useState<"" | PaymentMethodEnum>("");
  const [page, setPage] = useState(1);
  const params: PaymentFilters = { page, ...(status ? { status } : {}), ...(method ? { method } : {}) };
  const list = useBillingPaymentsList(params);
  const data = list.data?.status === 200 ? list.data.data : undefined;
  return (
    <GlassCard as="section" className="stack">
      <div className="row">
        <SelectField
          label={t("billing.filters.status")}
          value={status}
          options={[{ value: "", label: t("billing.filters.all") }, ...PAYMENT_STATUSES.map((s) => ({ value: s, label: t(`billing.paymentStatus.${s}`) }))]}
          onChange={(e) => {
            setStatus(e.target.value as "" | PaymentStatusEnum);
            setPage(1);
          }}
        />
        <SelectField
          label={t("billing.filters.method")}
          value={method}
          options={[{ value: "", label: t("billing.filters.all") }, ...PAYMENT_METHODS.map((m) => ({ value: m, label: t(`billing.method.${m}`) }))]}
          onChange={(e) => {
            setMethod(e.target.value as "" | PaymentMethodEnum);
            setPage(1);
          }}
        />
      </div>
      {list.isPending ? <Skeleton style={{ height: 120 }} /> : null}
      {list.isError ? <p role="alert" className="ko">{t("common.error")}</p> : null}
      {data && !data.results.length ? <p className="muted">{t("billing.noPayment")}</p> : null}
      {data?.results.length ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t("billing.col.date")}</th>
                <th>{t("billing.col.invoice")}</th>
                <th>{t("billing.col.payer")}</th>
                <th>{t("billing.col.method")}</th>
                <th className="num">{t("billing.col.amount")}</th>
                <th>{t("billing.col.status")}</th>
                <th>{t("billing.col.receipt")}</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((payment) => (
                <tr key={payment.id} className={payment.status === "cancelled" ? "is-cancelled" : undefined}>
                  <td className="nowrap">{dateTimeText(payment.confirmed_at ?? payment.created_at, i18n.language)}</td>
                  <td>
                    <Link className="link nowrap" to={`/invoices/${payment.invoice}`}>
                      {payment.invoice_number}
                    </Link>
                    <div className="muted">{payment.client_name}</div>
                  </td>
                  <td>
                    {payment.payer_name || payment.client_name}
                    {payment.third_party ? (
                      <>
                        {" "}
                        <Badge tone="offer">{t("billing.thirdParty")}</Badge>
                      </>
                    ) : null}
                  </td>
                  <td>{t(`billing.method.${payment.method}`)}</td>
                  <td className="num nowrap">
                    <span className="amount">{formatXaf(payment.amount_xaf)}</span>
                  </td>
                  <td>
                    <div className="row">
                      <Badge tone={paymentTone(payment.status)}>{t(`billing.paymentStatus.${payment.status}`)}</Badge>
                      {payment.is_test ? <Badge>{t("billing.test")}</Badge> : null}
                    </div>
                  </td>
                  <td>
                    {payment.receipt_url ? (
                      <a className="link nowrap" href={payment.receipt_url} target="_blank" rel="noreferrer">
                        {payment.receipt_number || t("billing.receipt")}
                      </a>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <Pager
        page={page}
        hasNext={Boolean(data?.next)}
        onChange={setPage}
        labels={{ previous: t("billing.previous"), next: t("billing.next"), page: t("billing.page", { page }) }}
      />
    </GlassCard>
  );
}

export function InvoicesPage() {
  const { t } = useTranslation();
  const { canCollect } = useBillingRoles();
  const [tab, setTab] = useState<Tab>("invoices");
  const [creating, setCreating] = useState(false);
  const figures = useFigures();
  const money = (value: number | undefined) => (value === undefined ? "—" : formatXaf(value));
  return (
    <Page
      title={t("billing.title")}
      description={t("billing.intro")}
      actions={
        canCollect ? (
          <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setCreating(true)}>
            {t("billing.newButton")}
          </Button>
        ) : null
      }
    >
      <section className="kpis" aria-label={t("billing.figures")}>
        <Figure icon={Wallet} tone="warning" label={t("billing.toCollect")} value={money(figures.toCollect)} hint={t("billing.toCollectHint", { count: figures.unpaidCount ?? 0 })} />
        <Figure icon={HandCoins} tone="success" label={t("billing.collectedMonth")} value={money(figures.collected)} hint={t("billing.collectedHint")} />
        <Figure
          icon={AlarmClock}
          label={t("billing.overdueCount")}
          value={figures.overdue ?? "—"}
          hint={t("billing.overdueHint")}
          tone={figures.overdue ? "danger" : "neutral"}
        />
      </section>
      <div className="tabs" role="tablist" aria-label={t("billing.sections")}>
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`billing-tab-${key}`}
            aria-selected={tab === key}
            aria-controls={`billing-panel-${key}`}
            className="tabs__tab"
            onClick={() => setTab(key)}
          >
            {t(`billing.tab.${key}`)}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`billing-panel-${tab}`} aria-labelledby={`billing-tab-${tab}`}>
        {tab === "invoices" ? <InvoiceTable key="invoice" kind="invoice" /> : null}
        {tab === "quotes" ? <InvoiceTable key="quote" kind="quote" /> : null}
        {tab === "credits" ? <InvoiceTable key="credit" kind="credit" /> : null}
        {tab === "payments" ? <PaymentTable /> : null}
      </div>
      {creating ? <InvoiceFormSheet open onClose={() => setCreating(false)} /> : null}
    </Page>
  );
}
